import { useState } from "react";
import Button from "@/common/ui/Button";
import { LightText2 } from "@/common/ui/Text";
import { toastMessage } from "@/utils/helper/myToast";
import { webaccess } from "@/services/employee.services";
import { Employee } from "@/types/global";

interface WebAccessProps {
  data: any;
  setRowSelection: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  onClose: () => void;
  afterCall?: () => void;
  selectionType: string;
  accessCounts: AccessCounts;
  mode: "grant" | "revoke";
  employeesData: Employee[]
}

interface AccessCounts {
  totalEmps: number;
  noWebaccess: number;
  hasWebaccess: number;
  exEmployes: number;
  disabledWebAccess: number;
  accountOwner: number;
}

// ----------------- HELPER -----------------
function generateAccessMessage({
  selectionType,
  mode,
  data,
  accessCounts,
}: {
  selectionType: string;
  mode: "grant" | "revoke";
  data: any;
  accessCounts: AccessCounts;
}): { content: string; successToast: string; showOkOnly: boolean } {
  if (selectionType === "single") {
    return {
      content: `Are you sure you want to ${
        mode === "grant" ? "enable" : "disable"
      } web access for ${data?.empName?.label}?`,
      successToast:
        mode === "grant"
          ? "The web access has been enabled for the employee!"
          : "The web access has been disabled for the employee!",
      showOkOnly: false,
    };
  }

  const alreadyCount =
    mode === "grant" ? accessCounts.hasWebaccess || 0 : accessCounts.disabledWebAccess || 0;
  const exCount = accessCounts.exEmployes || 0;
  const total = accessCounts.noWebaccess || 0;
  const remaining = total - alreadyCount;

  // ---------------- SPECIAL CASES ----------------
  if (mode === "grant" && alreadyCount === total && (total != 0 && exCount === 0)) {
    return {
      content: "All selected employees already have web access enabled.",
      successToast: "",
      showOkOnly: true,
    };
  }

  if (mode === "revoke" && alreadyCount === total && (total != 0 && exCount === 0)) {
    return {
      content: "All selected employees already have web access disabled.",
      successToast: "",
      showOkOnly: true,
    };
  }
  
  if (mode === "grant" && exCount > 0 && total == 0 && !accessCounts.accountOwner) {
    return {
      content: "All selected employees are marked as ex-employees. Web access cannot be enabled.",
      successToast: "",
      showOkOnly: true,
    };
  }
    if (mode === "revoke" && exCount > 0 && total == 0 && !accessCounts.accountOwner) {
    return {
      content: "All selected employees are marked as ex-employees. Web access cannot be modified.",
      successToast: "",
      showOkOnly: true,
    };
  }  

  if (accessCounts.accountOwner && mode === "grant" && total === 0 && !exCount) {
    return {
      content: "The account owner already has web access enabled by default.",
      successToast: "",
      showOkOnly: true,
    };
  }

  if (accessCounts.accountOwner && mode === "revoke" && total === 0 && !exCount) {
    return {
      content: "Web access cannot be disabled for the account owner.",
      successToast: "",
      showOkOnly: true,
    };
  }

  if(accessCounts.accountOwner && exCount && total === 0){
    return {
      content: "Selected employees are either ex-employees or account owners, and their web access cannot be modified",
      successToast: "",
      showOkOnly: true,
    };
  }

  // ---------------- DEFAULT FLOW ----------------
  let parts: string[] = [];

  // Already enabled/disabled
  if (alreadyCount > 0) {
    parts.push(
      `${alreadyCount} ${alreadyCount > 1 ? "employees" : "employee"} already ${alreadyCount == 1 ? 'has' : 'have'} web access ${
        mode === "grant" ? "enabled" : "disabled"
      }`

    );
  }

  // Ex-employees
  if (exCount > 0) {
    const exLabel = `${exCount} ${exCount > 1 ? "are" : "is"} ex-employee${exCount > 1 ? "s" : ""}`;
    if (alreadyCount > 0) {
      parts[parts.length - 1] += `, and ${exLabel}`;
    } else {
      parts.push(exLabel);
    }
  }

  // Account owner restriction
  if (accessCounts.accountOwner) {
    const ownerText =
      mode === "grant"
        ? `${accessCounts.accountOwner} is account owner`
        : `${accessCounts.accountOwner} is account owner`;

    if (alreadyCount > 0 || exCount > 0) {
      parts[parts.length - 1] += `, and ${ownerText}`;
    } else {
      parts.push(ownerText);
    }
  }

  // Remaining employees
  if (remaining > 0) {
    parts.push(
      `Are you sure you want to ${
        mode === "grant" ? "enable" : "disable"
      } web access for the ${alreadyCount === 0 && exCount === 0 && !accessCounts.accountOwner ? "" : "remaining"} ${remaining} ${
        remaining > 1 ? "employees" : "employee"
      }?`
    );
  }  

  const allAlreadyDone =
    (mode === "grant" && accessCounts.hasWebaccess - accessCounts.noWebaccess === 0) ||
    (mode === "revoke" && accessCounts.disabledWebAccess - accessCounts.noWebaccess === 0);

  if (allAlreadyDone) {
    return {
      content: parts.join(". "),
      successToast: "",
      showOkOnly: true,
    };
  }

  return {
    content: parts.join(". "),
    successToast:
      mode === "grant"
        ? "The web access has been enabled for the employees!"
        : "The web access has been disabled for the employees!",
    showOkOnly: false,
  };
}

// ----------------- COMPONENT -----------------
const WebAccess = ({
  data,
  setRowSelection,
  afterCall,
  onClose,
  selectionType,
  accessCounts,
  mode,
  employeesData
}: WebAccessProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const message = generateAccessMessage({ selectionType, mode, data, accessCounts });  

  const handleCall = async () => {
    setIsLoading(true);
    try {
      let filteredIds = data;

      if (selectionType !== "single") {
        filteredIds = data.filter((id: string) => {
          const emp = employeesData.find((e: any) => e.empExtId === id);
          console.log("sdfsfd", emp);
          
          if (!emp) return false;          
          if (mode === "grant") {
            return emp.webAccess === false;
          }
          if (mode === "revoke") {
            return emp.webAccess === true;
          }
          return false;
        });
      }
      const payload =
        selectionType === "single"
          ? { empExtIds: [data.empExtId], mode: data.webAccess ? "revoke" : "grant" }
          : { empExtIds: filteredIds, mode };

      const res: any = await webaccess(payload);

      if (res?.status === 200) {
        if(selectionType != "single"){
          setRowSelection({});
        }
        if (afterCall) {
            setTimeout(() => {
              afterCall();
          }, 10);
        }
        onClose();        
        const { message, isError } = res?.data || {};
        return toastMessage(message, isError);
      } else {
        console.log("Error", res);
        const { message: msg, isError } = res?.response?.data || {};
        if (msg) return toastMessage(msg, isError);
        if (res.message) return toastMessage(res.message, true);
        onClose();
      }
      onClose();
    } catch (error) {
      console.error("Error updating employee web access", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-3 md:p-4 relative">
      <LightText2 text={message.content} />

      <div className="flex justify-end pt-5">
        {message.showOkOnly ? (
          <Button variant="outline" size="small" onClick={onClose}>
            Ok
          </Button>
        ) : (
          <div className="flex justify-between gap-4">
            <Button variant="outline" size="small" onClick={onClose}>
              No
            </Button>
            <Button
              variant="outline-danger"
              size="small"
              isLoading={isLoading}
              onClick={handleCall}
              disabled={
                selectionType != "single" && mode == "grant" && accessCounts.hasWebaccess - accessCounts.noWebaccess == 0
                  ? true
                  : selectionType != "single" && mode == "revoke" && accessCounts.disabledWebAccess - accessCounts.noWebaccess == 0
                  ? true
                  : false
              }
            >
              Yes
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default WebAccess;
